const $$splitComponentImporter = () => import('/app/src/routes/{-$locale}/profile/$id.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import i18n from "@/lib/i18n";
import { dehydrate } from "@tanstack/react-query";
import { getDefaultProfilePageVariables, profilePageQueryOptions } from "@/queries/profile-gql";
import { getUserInformation } from "@/queries/profiles";
import { getFetchedQuery } from "@/lib/get-fetched-query";
import { getQueryClient } from "@/lib/client";
import { cn } from "@/lib/utils";
type LinkedAccount = {
  identityProviderId: string;
  displayName: string;
};
export const Route = createFileRoute("/{-$locale}/profile/$id")({
  component: lazyRouteComponent($$splitComponentImporter, 'component'),
  loader: async ({
    context,
    params
  }) => {
    const {
      queryClient,
      session
    } = context;
    const {
      id
    } = params;
    const profileVariables = getDefaultProfilePageVariables(id);
    await Promise.allSettled([queryClient.ensureQueryData(profilePageQueryOptions(profileVariables)), queryClient.ensureQueryData({
      queryKey: ["profile-information", {
        id
      }],
      queryFn: () => getUserInformation(id).catch(() => null),
      staleTime: 60_000
    })]);
    return {
      id,
      dehydratedState: dehydrate(queryClient),
      userId: session?.user.email.split("@")[0]
    };
  },
  head: ctx => {
    const {
      params
    } = ctx;
    const queryClient = getQueryClient();
    const profileVariables = getDefaultProfilePageVariables(params.id);
    const profileQueryKey = profilePageQueryOptions(profileVariables).queryKey;
    if (!ctx.loaderData) {
      return {
        meta: [{
          title: i18n.t("profile.meta.notFoundTitle"),
          description: i18n.t("profile.meta.notFoundDescription")
        }]
      };
    }
    const profile = getFetchedQuery<ProfilePageProfile | null>(queryClient, ctx.loaderData?.dehydratedState, profileQueryKey);
    if (!profile) {
      return {
        meta: [{
          title: i18n.t("profile.meta.notFoundTitle"),
          description: i18n.t("profile.meta.notFoundDescription")
        }]
      };
    }
    return {
      meta: [{
        title: i18n.t("profile.meta.title", {
          name: profile.displayName ?? i18n.t("profile.meta.defaultName")
        })
      }, {
        name: "description",
        content: i18n.t("profile.meta.description", {
          name: profile.displayName ?? i18n.t("profile.meta.defaultDescriptionName")
        })
      }]
    };
  }
});
export function EpicPlatinumIcon({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 15" className={cn("svg", className)} {...props}>
      <path fillRule="evenodd" clipRule="evenodd" d="M8.82469 5.7203C8.10017 4.28067 7.34052 2.77122 7.51834 0C6.90611 0.01125 4.43223 1.59312 3.97056 5.34875C3.48704 4.8144 3.24026 3.04552 3.33333 2.32187C1.13777 4.1775 0 6.56 0 9.21813C0 12.4019 1.90556 15 4.97945 15C8.05804 15 10 12.6544 10 9.8275C10 8.05565 9.42438 6.91189 8.82469 5.7203ZM4.99966 13.9598C5.83378 13.9598 6.50997 13.5934 6.50997 13.1415C6.50997 12.8016 6.12752 12.5101 5.58307 12.3865C5.44824 12.0795 5.37724 11.746 5.37724 11.4062C5.37724 11.3212 5.38389 11.237 5.39689 11.1541C6.45872 10.9664 7.2652 10.0392 7.2652 8.92337V7.57032L7.26527 7.56325C7.26527 7.06278 6.25098 6.65707 4.9998 6.65707C3.74862 6.65707 2.73433 7.06278 2.73433 7.56325L2.73427 8.92337C2.73427 10.0391 3.54067 10.9663 4.60242 11.1541C4.61543 11.237 4.62209 11.3212 4.62209 11.4062C4.62209 11.746 4.55109 12.0795 4.41626 12.3865C3.8718 12.5101 3.48935 12.8016 3.48935 13.1415C3.48935 13.5934 4.16554 13.9598 4.99966 13.9598Z" fill="currentColor" />
    </svg>;
}